import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PlayCircleOutlineRoundedIcon from "@mui/icons-material/PlayCircleOutlineRounded";
import {
  Box,
  Button,
  Container,
  FormControl,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "lib/api/api";
import Meta from "../Meta/Meta";

const specializations = [
  "consultingPage.booking.specializations.abroadStudy",
  "consultingPage.booking.specializations.careerPath",
  "consultingPage.booking.specializations.ausbildung",
  "consultingPage.booking.specializations.languagePlan",
];

const getSpecializationLabel = (key, isArabic, t) => {
  const fallbacks = {
    "consultingPage.booking.specializations.abroadStudy": isArabic ? "الدراسة في الخارج" : "Study Abroad",
    "consultingPage.booking.specializations.careerPath": isArabic ? "المسار المهني" : "Career Path",
    "consultingPage.booking.specializations.ausbildung": isArabic ? "التدريب المهني (Ausbildung)" : "Ausbildung (Vocational Training)",
    "consultingPage.booking.specializations.languagePlan": isArabic ? "خطة اللغة" : "Language Plan",
  };
  return t(key, fallbacks[key] || key);
};

export default function ConsultingPage() {
  const { t, i18n } = useTranslation(["ui"]);
  const isArabic = i18n.language === "ar";
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [specialization, setSpecialization] = useState("");
  const [message, setMessage] = useState("");
  const [selectedSlot, setSelectedSlot] = useState("");
  const [availableSchedules, setAvailableSchedules] = useState([]);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [showAlreadyBookedModal, setShowAlreadyBookedModal] = useState(false);
  const [infoModal, setInfoModal] = useState({ open: false, title: "", message: "", type: "info" });
  const [selectOpen, setSelectOpen] = useState(false);

  // Close only when main page scrolls, not when scrolling inside the dropdown itself
  useEffect(() => {
    const handleScroll = () => { if (selectOpen) setSelectOpen(false); };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [selectOpen]);

  const fetchSchedules = useCallback(async () => {
    try {
      const res = await api.get('/consultations/schedule/available');
      setAvailableSchedules(res.data);
    } catch (err) {
      console.error("Error fetching schedules", err);
    }
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
    fetchSchedules();
  }, [fetchSchedules]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedSlot) {
      setInfoModal({
        open: true,
        title: t("ui.error", isArabic ? "خطأ" : "Error"),
        message: t("consultingPage.booking.selectSlotRequired", isArabic ? "الرجاء تحديد موعد مفضل." : "Please select a preferred time slot."),
        type: "error"
      });
      return;
    }
    try {
      await api.post('/consultations/requests', {
        fullName,
        email,
        phone,
        specialization: getSpecializationLabel(specialization, isArabic, t),
        message,
        scheduleId: selectedSlot
      });
      setInfoModal({
        open: true,
        title: t("ui.success", isArabic ? "نجاح" : "Success"),
        message: t("consultingPage.booking.success", isArabic ? "سنرسل لك التأكيد عبر البريد الإلكتروني." : "We will send you the confirmation per email."),
        type: "success"
      });
      setFullName("");
      setEmail("");
      setPhone("");
      setSpecialization("");
      setMessage("");
      setSelectedSlot("");
      fetchSchedules(); // refresh available slots
    } catch (err) {
      if (err.response && err.response.data === "ALREADY_BOOKED") {
        setShowAlreadyBookedModal(true);
      } else if (err.response && err.response.data === "SLOT_UNAVAILABLE") {
        setInfoModal({
          open: true,
          title: t("ui.error", isArabic ? "خطأ" : "Error"),
          message: t("consultingPage.booking.slotUnavailable", isArabic ? "هذا الموعد لم يعد متاحاً." : "This slot is no longer available."),
          type: "error"
        });
      } else {
        console.error("Error booking session", err);
        setInfoModal({
          open: true,
          title: t("ui.error", isArabic ? "خطأ" : "Error"),
          message: t("consultingPage.booking.error", isArabic ? "حدث خطأ أثناء الحجز. يرجى المحاولة مرة أخرى." : "An error occurred while booking. Please try again."),
          type: "error"
        });
      }
    }
  };

  return (
    <Box dir={isArabic ? "rtl" : "ltr"}>
      <Meta
        title="ALGermanist - Career Consultation"
        description="Book a career consultation for university admission, Ausbildung, or career planning with Arabic-speaking guidance."
        keywords="career consultation, German university admission, Ausbildung consulting, ALGermanist"
      />

      <Box sx={{ bgcolor: "#F7F8F9", py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={5}>
              <Box sx={{ position: "relative" }}>
                <Box
                  component="img"
                  src="/avatars/avatar1.jpg"
                  alt="Consultant"
                  sx={{
                    width: "100%",
                    height: { xs: 360, md: 520 },
                    objectFit: "cover",
                    borderRadius: 4,
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 20,
                    left: isArabic ? "auto" : 20,
                    right: isArabic ? 20 : "auto",
                    backgroundColor: "rgba(0,0,0,0.65)",
                    borderRadius: 3,
                    px: 2.5,
                    py: 1.5,
                    color: "#fff",
                    textAlign: isArabic ? "right" : "left",
                    maxWidth: "80%",
                  }}
                >
                  <Typography fontWeight={800} sx={{ fontSize: "1rem" }}>
                    {t("consultingPage.consultant.name")}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {t("consultingPage.consultant.role")}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={7}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 5 },
                  borderRadius: 4,
                  bgcolor: "white",
                  boxShadow: "0 20px 60px rgba(0,0,0,0.08)",
                }}
              >
                <Typography
                  variant={isMobile ? "h4" : "h3"}
                  fontWeight={800}
                  sx={{ mb: 2, textAlign: isArabic ? "right" : "left" }}
                >
                  {t("consultingPage.hero.title")}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 4, whiteSpace: "pre-line", textAlign: isArabic ? "right" : "left" }}
                >
                  {t("consultingPage.hero.subtitle")}
                </Typography>

                <Grid container spacing={2}>
                  {[
                    {
                      label: t("consultingPage.hero.duration"),
                      value: t("consultingPage.hero.durationValue"),
                    },
                    {
                      label: t("consultingPage.hero.price"),
                      value: t("consultingPage.hero.priceValue"),
                    },
                    {
                      label: t("consultingPage.hero.method"),
                      value: t("consultingPage.hero.methodValue"),
                    },
                    {
                      label: t("consultingPage.hero.rating"),
                      value: t("consultingPage.hero.ratingValue"),
                    },
                  ].map((item, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <Box sx={{ p: 2, bgcolor: "#F6F8F8", borderRadius: 3 }}>
                        <Typography fontWeight={700} sx={{ mb: 0.5 }}>
                          {item.label}
                        </Typography>
                        <Typography>{item.value}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
        <Typography
          variant="h5"
          fontWeight={800}
          sx={{ mb: 3, textAlign: isArabic ? "right" : "left" }}
        >
          {t("consultingPage.listenSection.title")}
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mb: 4, textAlign: isArabic ? "right" : "left" }}
        >
          {t("consultingPage.listenSection.subtitle")}
        </Typography>
        <Box
          sx={{
            backgroundColor: "#111",
            borderRadius: 4,
            minHeight: 320,
            position: "relative",
            color: "white",
            display: "grid",
            placeItems: "center",
            textAlign: "center",
          }}
        >
          <PlayCircleOutlineRoundedIcon sx={{ fontSize: 68, opacity: 0.4 }} />
          <Typography sx={{ mt: 2, opacity: 0.85 }}>
            {t("consultingPage.listenSection.videoPlaceholder")}
          </Typography>
        </Box>
      </Container>

      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Typography
              variant="h5"
              fontWeight={800}
              sx={{ mb: 3, textAlign: isArabic ? "right" : "left" }}
            >
              {t("consultingPage.included.title")}
            </Typography>
            <List sx={{ direction: isArabic ? "rtl" : "ltr" }}>
              {t("consultingPage.included.items", { returnObjects: true }).map(
                (item, index) => (
                  <ListItem key={index} disableGutters>
                    <ListItemIcon sx={{ minWidth: 34, color: "#FFD600" }}>
                      <CheckCircleOutlineIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={item}
                      primaryTypographyProps={{
                        textAlign: isArabic ? "right" : "left",
                      }}
                    />
                  </ListItem>
                )
              )}
            </List>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography
              variant="h5"
              fontWeight={800}
              sx={{ mb: 3, textAlign: isArabic ? "right" : "left" }}
            >
              {t("consultingPage.consultant.title")}
            </Typography>
            <Paper
              elevation={1}
              sx={{ p: 4, borderRadius: 4, bgcolor: "#F5F7F9" }}
            >
              <Typography
                variant="h6"
                fontWeight={800}
                sx={{ mb: 2, textAlign: isArabic ? "right" : "left" }}
              >
                {t("consultingPage.consultant.name")}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 3, textAlign: isArabic ? "right" : "left" }}
              >
                {t("consultingPage.consultant.description")}
              </Typography>
              <Stack
                spacing={2}
                sx={{ textAlign: isArabic ? "right" : "left" }}
              >
                {t("consultingPage.consultant.details", { returnObjects: true }).map(
                  (detail, index) => (
                    <Box key={index} sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          bgcolor: "#FFD600",
                          borderRadius: "50%",
                          mt: 0.8,
                        }}
                      />
                      <Typography>{detail}</Typography>
                    </Box>
                  )
                )}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      <Container maxWidth="lg" sx={{ pb: { xs: 6, md: 10 } }}>
        <Grid container justifyContent={isArabic ? "flex-start" : "flex-end"}>
          <Grid item xs={12} md={6}>
            <Paper
              id="consulting-booking-form"
              elevation={1}
              dir={isArabic ? "rtl" : "ltr"}
              sx={{ px: { xs: 3, md: 6 }, py: { xs: 4, md: 6 }, borderRadius: 4 }}
            >
              <Typography
                variant="h5"
                fontWeight={800}
                sx={{ mb: 2, textAlign: isArabic ? "right" : "left" }}
              >
                {t("consultingPage.booking.title")}
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: 4, textAlign: isArabic ? "right" : "left" }}
              >
                {t("consultingPage.booking.subtitle", isArabic ? "احجز استشارتك المجانية الأولى" : "Book your first free consultation")}
              </Typography>
              <Box component="form" onSubmit={handleSubmit} dir={isArabic ? "rtl" : "ltr"}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      placeholder={t("consultingPage.booking.fullName", isArabic ? "الاسم الكامل" : "Full Name")}
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      fullWidth
                      required
                      inputProps={{
                        dir: isArabic ? "rtl" : "ltr",
                        style: { textAlign: isArabic ? "right" : "left" },
                      }}
                      sx={{ direction: isArabic ? "rtl" : "ltr" }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      placeholder={t("consultingPage.booking.email", isArabic ? "البريد الإلكتروني" : "Email")}
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      fullWidth
                      required
                      inputProps={{
                        dir: isArabic ? "rtl" : "ltr",
                        style: { textAlign: isArabic ? "right" : "left" },
                      }}
                      sx={{ direction: isArabic ? "rtl" : "ltr" }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      placeholder={t("consultingPage.booking.phone", isArabic ? "رقم الهاتف" : "Phone Number")}
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      fullWidth
                      required
                      inputProps={{
                        dir: isArabic ? "rtl" : "ltr",
                        style: { textAlign: isArabic ? "right" : "left" },
                      }}
                      sx={{ direction: isArabic ? "rtl" : "ltr" }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth sx={{ direction: isArabic ? "rtl" : "ltr" }}>
                      <Select
                        displayEmpty
                        value={specialization}
                        onChange={(e) => setSpecialization(e.target.value)}
                        open={selectOpen}
                        onOpen={() => setSelectOpen(true)}
                        onClose={() => setSelectOpen(false)}
                        sx={{ direction: isArabic ? "rtl" : "ltr" }}
                        MenuProps={{ disableScrollLock: true }}
                        renderValue={(selected) =>
                          selected ? getSpecializationLabel(selected, isArabic, t) : <em>{t("consultingPage.booking.specializationPlaceholder", isArabic ? "اختر التخصص" : "Select Specialization")}</em>
                        }
                      >
                        <MenuItem disabled value="">
                          <em>{t("consultingPage.booking.specializationPlaceholder", isArabic ? "اختر التخصص" : "Select Specialization")}</em>
                        </MenuItem>
                        {specializations.map((key) => (
                          <MenuItem key={key} value={key}>
                            {getSpecializationLabel(key, isArabic, t)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      placeholder={t("consultingPage.booking.questionPlaceholder")}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      multiline
                      minRows={4}
                      fullWidth
                      inputProps={{
                        dir: isArabic ? "rtl" : "ltr",
                        style: { textAlign: isArabic ? "right" : "left" },
                      }}
                      sx={{ direction: isArabic ? "rtl" : "ltr" }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      sx={{ mb: 2, textAlign: isArabic ? "right" : "left" }}
                    >
                      {t("consultingPage.booking.preferredTimeTitle")}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1.5,
                        justifyContent: isArabic ? "flex-start" : "flex-end"
                      }}
                    >
                      {availableSchedules.length > 0 ? (
                        availableSchedules.map((schedule) => {
                          const dateObj = new Date(`${schedule.availableDate}T${schedule.availableTime}`);
                          const langCode = isArabic ? 'ar-EG' : 'en-US';
                          const formattedDateStr = dateObj.toLocaleDateString(langCode, {
                            weekday: 'short',
                            day: 'numeric',
                            month: 'short'
                          });
                          const formattedTimeStr = schedule.availableTime.substring(0, 5);
                          const slotLabel = `${formattedDateStr} - ${formattedTimeStr}`;
                          return (
                            <Button
                              key={schedule.id}
                              variant={selectedSlot === schedule.id ? "contained" : "outlined"}
                              onClick={() => setSelectedSlot(schedule.id)}
                              sx={{
                                borderRadius: 3,
                                textTransform: "none",
                                bgcolor: selectedSlot === schedule.id ? "#FFD600" : "transparent",
                                color: selectedSlot === schedule.id ? "black" : "inherit",
                                borderColor: "#E3E7EA",
                              }}
                            >
                              {slotLabel}
                            </Button>
                          );
                        })
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {t("consultingPage.booking.noSlotsAvailable", "No available slots at the moment.")}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      size="large"
                      fullWidth
                      sx={{
                        backgroundColor: "#FFD600",
                        color: "#111",
                        fontWeight: 700,
                        textTransform: "none",
                        borderRadius: 4,
                        py: 1.6,
                        boxShadow: "none",
                        "&:hover": { backgroundColor: "#e5c400" },
                      }}
                    >
                      {t("consultingPage.booking.bookFirstFree", isArabic ? "احجز جلستك المجانية الأولى" : "Book your first free session")}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      <Dialog open={showAlreadyBookedModal} onClose={() => setShowAlreadyBookedModal(false)}>
        <DialogTitle sx={{ textAlign: isArabic ? "right" : "left", fontWeight: 'bold' }}>
          {t("consultingPage.modal.alreadyBookedTitle", isArabic ? "تم حجز الجلسة مسبقاً" : "Session Already Booked")}
        </DialogTitle>
        <DialogContent sx={{ textAlign: isArabic ? "right" : "left" }}>
          <Typography>
            {t("consultingPage.modal.alreadyBookedMessage", isArabic ? "لقد قمت بحجز جلسة مجانية من قبل. يجب عليك الدفع لحجز جلسة أخرى." : "You have already booked a free session before. You have to pay to take another session.")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setShowAlreadyBookedModal(false)} color="inherit">
            {t("ui.cancel", isArabic ? "إلغاء" : "Cancel")}
          </Button>
          <Button variant="contained" sx={{ backgroundColor: '#FFD600', color: 'black', '&:hover': { backgroundColor: '#e5c400' } }} onClick={() => { /* Dead button for now */ }}>
            {t("consultingPage.modal.pay", isArabic ? "ادفع" : "Pay")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={infoModal.open} onClose={() => setInfoModal({ ...infoModal, open: false })}>
        <DialogTitle sx={{ textAlign: isArabic ? "right" : "left", fontWeight: 'bold', color: infoModal.type === 'error' ? 'error.main' : 'success.main' }}>
          {infoModal.title}
        </DialogTitle>
        <DialogContent sx={{ textAlign: isArabic ? "right" : "left" }}>
          <Typography>
            {infoModal.message}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setInfoModal({ ...infoModal, open: false })} variant="contained" sx={{ backgroundColor: infoModal.type === 'error' ? 'inherit' : '#FFD600', color: infoModal.type === 'error' ? 'inherit' : 'black', '&:hover': { backgroundColor: infoModal.type === 'error' ? 'inherit' : '#e5c400' } }}>
            {t("ui.ok", isArabic ? "حسناً" : "OK")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
